/**
 * admin.tsx
 * ----------------
 * Entry point for admin portal app.
 */

import * as React from 'react';
import { ConnectedRouter, routerMiddleware } from 'connected-react-router';
import { createBrowserHistory } from 'history';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import { StaticContext } from 'react-router';
import { Route, Switch, RouteComponentProps } from 'react-router-dom';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunkMiddleware from 'redux-thunk';

import {
    preloadActiveLiveSiteEvents, preloadLiveSiteEvent, preloadError, preloadRecentUpdates,
    preloadResolvedLiveSiteEvents, preloadServices, preloadStatuses,
} from './actions/preload';
import { Bootstrapper } from './components/shared/Bootstrapper';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { HistoryView } from './components/views/HistoryView';
import { LiveSiteEventView } from './components/views/LiveSiteEventView';
import { PublicPortalView } from './components/views/PublicPortalView';
import { StatusView } from './components/views/StatusView';
import { PublicPortalStatus, PublicPortalViewPivot } from './constants/ui';
import { defineIconsUsingSvg } from './icons';
import { ErrorResponse } from './models/common';
import {
    PreloadActiveLiveSiteEventsPayload, PreloadLiveSiteEventPayload, PreloadRecentUpdatesPayload,
    PreloadResolvedLiveSiteEventsPayload, PreloadServicesPayload, PreloadStatusesPayload,
} from './models/payloads/preload';
import { publicAppReducer } from './reducers/status';
import { parseSearchString } from './utilities/urls';

interface PublicPortalViewMatches {
    id?: string;
    pivot?: PublicPortalViewPivot;
}

(() => {
    const history = createBrowserHistory();

    // TODO: enable dev tools
    const store = createStore(
        publicAppReducer(history),
        composeWithDevTools(
            applyMiddleware(thunkMiddleware),
            applyMiddleware(routerMiddleware(history))
        )
    );

    // Define SVG-based icon language
    defineIconsUsingSvg();

    // Specify preloads and preload errors
    const statusViewPreloads = [
        {
            action: (blob: unknown) => preloadActiveLiveSiteEvents(blob as PreloadActiveLiveSiteEventsPayload),
            name: 'global_ActiveLiveSiteEvents',
        },
        {
            action: (blob: unknown) => preloadLiveSiteEvent(blob as PreloadLiveSiteEventPayload),
            name: 'global_LiveSiteEvent',
        },
        {
            action: (blob: unknown) => preloadRecentUpdates(blob as PreloadRecentUpdatesPayload),
            name: 'global_RecentUpdates',
        },
        {
            action: (blob: unknown) => preloadResolvedLiveSiteEvents(blob as PreloadResolvedLiveSiteEventsPayload),
            name: 'global_ResolvedLiveSiteEvents',
        },
        { action: (blob: unknown) => preloadServices(blob as PreloadServicesPayload), name: 'global_Services' },
        { action: (blob: unknown) => preloadStatuses(blob as PreloadStatusesPayload), name: 'global_Statuses' },
    ];

    const statusViewErrors = [
        { action: (blob: unknown) => preloadError(blob as ErrorResponse), name: 'global_Error' },
    ];

    /* eslint-disable @typescript-eslint/no-explicit-any */
    // Justification: We have no control over the use of any type here.
    function renderViews (props: RouteComponentProps<PublicPortalViewMatches, StaticContext>) {
    /* eslint-enable @typescript-eslint/no-explicit-any */
        const { match } = props;
        const { params } = match;
        const { pivot } = params;
        const portalProps = {
            ...pivot !== undefined ? { pivot } : { pivot: PublicPortalStatus },
        };

        return (
            <ErrorBoundary>
                <Bootstrapper errors={statusViewErrors} preloads={statusViewPreloads}>
                    <PublicPortalView {...portalProps}>
                        <Switch>
                            <Route
                                exact
                                path="/"
                                render={() => <StatusView liveSiteEventUrl={id => `/event/${id}`} />} />
                            <Route
                                exact
                                path="/event/:id"
                                render={props =>
                                    <LiveSiteEventView
                                        basePageTitle="Visual Studio Service Status"
                                        id={parseInt(props.match.params.id)} />} />
                            <Route
                                exact
                                path="/history"
                                render={props => {
                                    // Extract search string parameters relevant to /history
                                    const parameters = parseSearchString(props.location.search);
                                    const geo = 'geo' in parameters ? parameters.geo : undefined;
                                    const sev = 'sev' in parameters ? parameters.sev : undefined;
                                    const svc = 'svc' in parameters ? parameters.svc : undefined;
                                    const t = 't' in parameters ? parameters.t : undefined;

                                    return (
                                        <HistoryView
                                            basePageTitle="Visual Studio Service Status"
                                            geo={geo}
                                            historyUrl="/history"
                                            liveSiteEventUrl={id => `/event/${id}`}
                                            sev={sev}
                                            svc={svc}
                                            t={t} />
                                    );
                                }} />
                            <Route
                                exact
                                path="/status"
                                render={() => <StatusView liveSiteEventUrl={id => `/event/${id}`} />} />
                        </Switch>
                    </PublicPortalView>
                </Bootstrapper>
            </ErrorBoundary>
        );
    }

    render(
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <Switch>
                    <Route path="/:pivot/:id" render={renderViews} />
                    <Route path="/:pivot" render={renderViews} />
                    <Route path="/" render={renderViews} />
                </Switch>
            </ConnectedRouter>
        </Provider>
        , document.getElementById('status-container'));
})();
